import {
  useDeleteOwnerMediaFileMutation,
  useUpdatePropertyMediaFileMutation,
  useUpdatePropertyMediaStatusMutation,
  useUploadOwnerMediaFileMutation,
} from "@/services/media";
import TransformedImage from "@components/TransformedImage";
import { EmeraldButton } from "@emerald-react/button";
import { EmeraldIcon } from "@emerald-react/icon";
import { EmeraldInputV2 } from "@emerald-react/input-v2";
import { EmeraldList, EmeraldListItem } from "@emerald-react/list";
import { EmeraldMenu } from "@emerald-react/menu";
import { EmeraldProgressSpinner } from "@emerald-react/progress-indicator";
import { useSortableItem } from "@hooks/useSortable";
import { useCurrentPropertyId, useSurvey } from "@hooks/useSurveySelectors";
import { AttachmentUploadsInterface } from "@shared-types/component.types";
import classNames from "classnames";
import { PDFDocument } from "pdf-lib";
import React, { startTransition, useOptimistic, useState } from "react";

const AttachmentThumbnailPreview = ({
  eachFile,
  fileType,
  setPdfError,
  allMediaFiles = [],
  onMoveFile,
}: AttachmentUploadsInterface) => {
  const [updatePropertyMediaFile] = useUpdatePropertyMediaFileMutation();
  const [updatePropertyMediaStatus] = useUpdatePropertyMediaStatusMutation();
  const [deleteOwnerMediaFile] = useDeleteOwnerMediaFileMutation();
  const [uploadOwnerMediaFile] = useUploadOwnerMediaFileMutation();

  const { attributes, listeners, setNodeRef, style, isDragging } =
    useSortableItem(eachFile?.mediaId || "");

  const survey = useSurvey();
  const currentPropertyId = useCurrentPropertyId() || "";

  const fileExtension = eachFile?.displayName?.replace(/\.\w+$/, "") || "";

  const [fileName, setFileName] = useState(fileExtension);
  const [enableEdit, isEnableEdit] = useState(false);
  const [visibility, setVisibility] = useState(eachFile?.isHidden);
  const [manualLoading, setManualLoading] = useState(false);
  const [optimisticLoading, setOptimisticLoading] =
    useOptimistic(manualLoading);

  // Helper to set optimistic loading state
  const setOptimisticLoadingState = (loading: boolean) => {
    setManualLoading(loading);
    if (loading) {
      startTransition(() => setOptimisticLoading(true));
    }
  };

  const [selectedPages, setSelectedPages] = useState(
    eachFile?.selectedPages || "",
  );
  const [previousSelectedPages, setPreviousSelectedPages] = useState(
    eachFile?.selectedPages || "",
  );
  const mediaType = fileType.toLocaleUpperCase();
  const isFlier = fileType === "fliers";
  const hasOriginalFile = !!eachFile?.modifiedFromMediaId;
  const isTempFile = eachFile?.mediaId.includes("temp-id-");

  // Determine if the file is a PDF (used for move button logic)
  const isPdfFile =
    !!eachFile?.pdfImageUrl ||
    eachFile?.keyName?.toLowerCase().endsWith(".pdf") ||
    eachFile?.cloudfrontUrl?.toLowerCase().endsWith(".pdf");

  // Common error handler
  const handleError = (_action: string, _error: unknown) => {
    console.error("Failed to update attachment");
  };

  // Common event handler props for preventing drag
  const dragPreventionProps = {
    onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
    onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
  };

  // Common spinner props
  const spinnerProps = {
    title: "Loading attachment",
    size: "medium" as const,
    isWhite: false,
    determinate: false,
  };

  // Helper to create common mutation payload
  const getBaseMutationPayload = () => ({
    mediaId: eachFile?.mediaId || "",
    mediaType,
    propertyId: currentPropertyId,
    surveyId: survey?.id || "",
    ownerType: "PROPERTY" as const,
  });

  // Get the source PDF URL - if this is a modified file, use the original source
  const getSourcePdfUrl = (): string => {
    if (eachFile?.modifiedFromMediaId) {
      const sourceFile = allMediaFiles.find(
        (file) => file.mediaId === eachFile.modifiedFromMediaId,
      );
      return sourceFile?.cloudfrontUrl || eachFile?.cloudfrontUrl || "";
    }
    return eachFile?.cloudfrontUrl || "";
  };

  // Helper to get thumbnail image URL
  const getThumbnailUrl = () => {
    if (eachFile?.pdfImageUrl) return `${eachFile.pdfImageUrl}?d=thumbnail`;
    const cloudfrontUrl = eachFile?.cloudfrontUrl || "";
    const hasExtension = cloudfrontUrl.split("/").pop()?.includes(".");
    return hasExtension
      ? `${cloudfrontUrl}?d=thumbnail`
      : `${cloudfrontUrl}_thumb`;
  };

  const handleRenameAttachment = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    isEnableEdit(false);
    if (e.target.value.trim() === fileExtension) return;
    setFileName(e.target.value);

    updatePropertyMediaFile({
      ...getBaseMutationPayload(),
      displayName: e.target.value,
    })
      .unwrap()
      .catch((error) => handleError("rename attachment", error));
  };

  const handleVisibilityStatus = () => {
    setVisibility(!visibility);

    updatePropertyMediaStatus({
      ...getBaseMutationPayload(),
      isHidden: !visibility,
    })
      .unwrap()
      .catch((error) => handleError("update attachment status", error));
  };

  const deleteHandler = async () => {
    setOptimisticLoadingState(true);

    try {
      await deleteOwnerMediaFile(getBaseMutationPayload()).unwrap();

      // If this is a modified file, also delete the original source file
      if (eachFile?.modifiedFromMediaId) {
        await deleteOwnerMediaFile({
          ...getBaseMutationPayload(),
          mediaId: eachFile.modifiedFromMediaId,
        }).unwrap();
      }
    } catch (error) {
      handleError("delete attachment", error);
    } finally {
      setManualLoading(false);
    }
  };

  const validatePdfAndPages = async (
    pdfUrl: string,
    pagesToValidate: string,
    existingArrayBuffer?: ArrayBuffer,
  ): Promise<{ valid: boolean; error?: string; arrayBuffer?: ArrayBuffer }> => {
    try {
      // Use existing arrayBuffer or fetch the PDF file
      let arrayBuffer: ArrayBuffer;
      if (existingArrayBuffer) {
        arrayBuffer = existingArrayBuffer;
      } else {
        const response = await fetch(pdfUrl);
        arrayBuffer = await response.arrayBuffer();
      }

      // Load the PDF document and check for encryption
      let pdfDoc: PDFDocument;
      pdfDoc = await PDFDocument.load(arrayBuffer, {
        ignoreEncryption: false,
      });

      const totalPages = pdfDoc.getPageCount();

      // Parse comma-separated page numbers
      const parts = pagesToValidate.split(",");

      for (const part of parts) {
        const trimmed = part.trim();
        const pageNum = parseInt(trimmed);

        if (isNaN(pageNum) || pageNum < 1 || pageNum > totalPages) {
          return {
            valid: false,
            error: `Invalid page number "${trimmed}". PDF has ${totalPages} pages.`,
          };
        }
      }

      return { valid: true, arrayBuffer };
    } catch (error) {
      // Check for specific encryption error
      if (error instanceof Error && error.message.includes("encrypted")) {
        return {
          valid: false,
          error: "This PDF is encrypted. Please upload an unencrypted version.",
        };
      }

      // Check for PDF loading errors
      if (
        error instanceof Error &&
        error.message.toLowerCase().includes("pdf")
      ) {
        return {
          valid: false,
          error: "Failed to load PDF. Please ensure the file is a valid PDF.",
        };
      }

      handleError("validate PDF", error);
      return {
        valid: false,
        error: "Failed to validate PDF. Please try again.",
      };
    }
  };

  // Reset PDF state helper
  const resetPdfState = () => {
    setPdfError?.("");
    setPreviousSelectedPages("");
    setSelectedPages("");
    setManualLoading(false);
  };

  const handleModifyPdf = async () => {
    if (!selectedPages.trim()) return;
    setPdfError?.("");

    // Check for page ranges (containing dashes)
    if (selectedPages.includes("-")) {
      setPdfError?.(
        "Page ranges are not supported, page numbers should be comma-separated",
      );
      return;
    }

    setOptimisticLoadingState(true);
    const previousValue = previousSelectedPages;

    try {
      // Fetch source PDF once and validate
      const sourcePdfUrl = getSourcePdfUrl();
      const response = await fetch(sourcePdfUrl);
      const arrayBuffer = await response.arrayBuffer();

      // Validate PDF and page numbers using the fetched arrayBuffer
      const validation = await validatePdfAndPages(
        sourcePdfUrl,
        selectedPages.trim(),
        arrayBuffer,
      );

      if (!validation.valid) {
        setPdfError?.(validation.error || "Validation failed");
        setManualLoading(false);
        // Restore previous value if it existed
        if (previousValue) {
          setSelectedPages(previousValue);
        }
        return;
      }

      // If this is already a modified file, delete it before creating a new modified version
      if (eachFile?.modifiedFromMediaId) {
        await deleteOwnerMediaFile(getBaseMutationPayload()).unwrap();
      }

      // Load the source PDF (already fetched during validation)
      const originalPdf = await PDFDocument.load(arrayBuffer);

      // Create a new PDF document
      const newPdf = await PDFDocument.create();

      // Parse the selected page numbers
      const pageNumbers = selectedPages
        .split(",")
        .map((p) => parseInt(p.trim()) - 1); // Convert to 0-based index

      // Copy selected pages to the new PDF
      const copiedPages = await newPdf.copyPages(originalPdf, pageNumbers);
      copiedPages.forEach((page) => {
        newPdf.addPage(page);
      });

      // Save the new PDF as bytes
      const pdfBytes = await newPdf.save();

      // Create a File object from the PDF bytes
      const modifiedFileName = `${eachFile?.displayName?.replace(".pdf", "")}_modified.pdf`;
      const pdfFile = new File([new Uint8Array(pdfBytes)], modifiedFileName, {
        type: "application/pdf",
      });

      // Prepare FormData with the actual file
      const formData = new FormData();
      formData.append("file", pdfFile);
      formData.append(
        "media",
        JSON.stringify({
          surveyId: survey?.id || "",
          user: localStorage.getItem("email") || "",
          mediaType,
          propertyId: currentPropertyId,
          modifiedFromMediaId:
            eachFile?.modifiedFromMediaId || eachFile?.mediaId,
          selectedPages: selectedPages.trim(),
        }),
      );

      await uploadOwnerMediaFile(formData).unwrap();
      resetPdfState();
    } catch (error) {
      handleError("modify PDF", error);
      setPdfError?.("Failed to modify PDF. Please try again.");
      setManualLoading(false);
      // Restore previous value if it existed
      if (previousValue) {
        setSelectedPages(previousValue);
      }
    }
  };

  const handleRestorePdf = () => {
    deleteOwnerMediaFile(getBaseMutationPayload())
      .unwrap()
      .catch((error) => handleError("restore PDF", error));
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames("flex transition-colors group", {
        "opacity-50 bg-slate-100": isDragging,
      })}
      {...attributes}
      {...listeners}
    >
      <div
        key={eachFile?.mediaId}
        className="mb-2 w-50 relative cursor-move thumbnail-container"
      >
        {!isTempFile ? (
          <>
            {optimisticLoading && (
              <EmeraldProgressSpinner
                {...spinnerProps}
                className="absolute top-2/5 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-50 z-10"
              />
            )}
            <TransformedImage
              imageUrl={getThumbnailUrl()}
              alt={`Attachment ${fileName}`}
              className={classNames(
                "w-50 h-37.5 bg-cover bg-center overflow-hidden",
                {
                  "opacity-30": visibility || optimisticLoading,
                },
              )}
              asBackground={true}
              showFallback={true}
            />
          </>
        ) : (
          <div className="w-50 h-37.5 flex justify-center items-center">
            <EmeraldProgressSpinner {...spinnerProps} />
          </div>
        )}
        {!optimisticLoading && !eachFile?.mediaId.includes("temp-id-") && (
          <div {...dragPreventionProps} className="absolute top-2 left-2">
            <EmeraldMenu
              triggerElement={<EmeraldIcon icon={"more_vert"} />}
              opened={false}
              closeOnOutsideClick={true}
              position={"auto"}
              positionX={50}
              positionY={50}
              dropdownWidth={500}
              className="bg-white rounded-full cursor-pointer z-10"
            >
              <EmeraldList>
                <EmeraldListItem
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVisibilityStatus();
                  }}
                >
                  <span>{visibility ? "Active" : "Inactive"}</span>
                </EmeraldListItem>
                <EmeraldListItem
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteHandler();
                  }}
                >
                  <span>Delete</span>
                </EmeraldListItem>
              </EmeraldList>
            </EmeraldMenu>
          </div>
        )}

        {!enableEdit ? (
          <div className="text-xs my-2 text-edit-icon flex items-center gap-1 min-w-0">
            <span className="truncate flex-1 min-w-0" title={fileName}>{fileName}</span>
            <div
              className="flex-shrink-0"
              {...dragPreventionProps}
              onClick={(e) => {
                e.stopPropagation();
                isEnableEdit(true);
              }}
            >
              <EmeraldIcon
                icon="edit"
                type="filled"
                variant="icon"
                className="align-bottom cursor-pointer"
              />
            </div>
          </div>
        ) : (
          <div {...dragPreventionProps}>
            <EmeraldInputV2
              type="text"
              label=""
              value={fileName}
              size="small"
              className="without-label my-2"
              isFocused
              onBlur={handleRenameAttachment}
            />
          </div>
        )}

        {/* Category move buttons: Image | Site Plan | Flyer */}
        {!isTempFile && !optimisticLoading && onMoveFile && (
          <div {...dragPreventionProps} className="flex gap-1 mt-1 mb-2">
            {/* Image: enabled for image files only */}
            <button
              disabled={isPdfFile || fileType === "images"}
              className={[
                "rounded-full border px-2 py-0.5 text-[11px] leading-tight transition-colors",
                fileType === "images"
                  ? "border-green-800 bg-green-800 text-white"
                  : isPdfFile
                    ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300"
                    : "cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-green-700",
              ].join(" ")}
              onClick={(e) => {
                e.stopPropagation();
                if (!isPdfFile && fileType !== "images" && eachFile?.mediaId) {
                  onMoveFile(
                    eachFile.mediaId,
                    fileType as "images" | "floorPlans" | "fliers",
                    "images",
                  );
                }
              }}
            >
              Image
            </button>
            {/* Site Plan: enabled for both images and PDFs */}
            <button
              disabled={fileType === "floorPlans" || hasOriginalFile}
              className={[
                "rounded-full border px-2 py-0.5 text-[11px] leading-tight transition-colors",
                fileType === "floorPlans"
                  ? "border-green-800 bg-green-800 text-white"
                  : hasOriginalFile
                    ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300"
                    : "cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-green-700",
              ].join(" ")}
              onClick={(e) => {
                e.stopPropagation();
                if (
                  fileType !== "floorPlans" &&
                  !hasOriginalFile &&
                  eachFile?.mediaId
                ) {
                  onMoveFile(
                    eachFile.mediaId,
                    fileType as "images" | "floorPlans" | "fliers",
                    "floorPlans",
                  );
                }
              }}
            >
              {survey?.sitePlanLabel || "Site Plans"}
            </button>
            {/* Flyer: enabled for PDF files only */}
            <button
              disabled={!isPdfFile || fileType === "fliers"}
              className={[
                "rounded-full border px-2 py-0.5 text-[11px] leading-tight transition-colors",
                fileType === "fliers"
                  ? "border-green-800 bg-green-800 text-white"
                  : !isPdfFile
                    ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300"
                    : "cursor-pointer border-gray-300 bg-white text-gray-700 hover:border-green-700",
              ].join(" ")}
              onClick={(e) => {
                e.stopPropagation();
                if (isPdfFile && fileType !== "fliers" && eachFile?.mediaId) {
                  onMoveFile(
                    eachFile.mediaId,
                    fileType as "images" | "floorPlans" | "fliers",
                    "fliers",
                  );
                }
              }}
            >
              {survey?.flyerLabel || "Flyers"}
            </button>
          </div>
        )}

        {isFlier && !isTempFile && (
          <div {...dragPreventionProps}>
            <EmeraldInputV2
              type="text"
              placeholder="e.g., 1,2,3"
              value={selectedPages}
              size="small"
              className="mb-2"
              onChange={(e) => {
                setSelectedPages(e.target.value);
                setPdfError?.("");
              }}
              disabled={optimisticLoading}
            />
            <div className="flex gap-x-2">
              <EmeraldButton
                type="Secondary"
                size="Small"
                className="media-modify-pdf"
                onClick={(e) => {
                  e.stopPropagation();
                  handleModifyPdf();
                }}
                disabled={optimisticLoading || !selectedPages.trim()}
              >
                Modify
              </EmeraldButton>
              {hasOriginalFile && (
                <EmeraldButton
                  size="Small"
                  className="media-restore-pdf"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRestorePdf();
                  }}
                  disabled={optimisticLoading}
                >
                  Restore PDF
                </EmeraldButton>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentThumbnailPreview;
